import type {
  VerdaCostLedgerEntry,
  VerdaDestroyOutcome,
  VerdaFinding,
  VerdaGpuAttentionItem,
  VerdaGpuInvoice,
  VerdaGpuServer,
  VerdaGpuServerStatus,
  VerdaGpuServerUsageRow,
} from '../../../types/models/Verda';
import { sumMoney } from '../../../utils/money';

// Pure rules of the staff GPU servers surface (docs/api-types/admin-verda.md "GPU servers (staff)").
// The backend re-checks every one of them and its refusal is shown verbatim; these only decide what
// the UI enables and how it explains a disabled action.

export type VerdaLabelColor = 'blue' | 'green' | 'red' | 'orange' | 'grey';

/** Every internal status, in lifecycle order. */
export const VERDA_GPU_STATUSES: VerdaGpuServerStatus[] = [
  'ordering',
  'deploy_unconfirmed',
  'provisioning',
  'running',
  'unpaid',
  'destroying',
  'destroyed',
  'failed',
  'lost',
];

/** Billing is over: refund and resolve apply. */
export const VERDA_GPU_TERMINAL_STATUSES: VerdaGpuServerStatus[] = ['destroyed', 'failed', 'lost'];

/** The Servers tab's default filter: every non-terminal status. */
export const VERDA_GPU_DEFAULT_STATUSES: VerdaGpuServerStatus[] = VERDA_GPU_STATUSES.filter(
  (status) => !VERDA_GPU_TERMINAL_STATUSES.includes(status),
);

const STATUS_LABELS: Record<VerdaGpuServerStatus, { label: string; color: VerdaLabelColor }> = {
  ordering: { label: 'Ordering', color: 'blue' },
  deploy_unconfirmed: { label: 'Deploy unconfirmed', color: 'orange' },
  provisioning: { label: 'Provisioning', color: 'blue' },
  running: { label: 'Running', color: 'green' },
  unpaid: { label: 'Unpaid', color: 'red' },
  destroying: { label: 'Destroying', color: 'orange' },
  destroyed: { label: 'Destroyed', color: 'grey' },
  failed: { label: 'Failed', color: 'red' },
  lost: { label: 'Lost', color: 'red' },
};

const isKnownStatus = (status: string): status is VerdaGpuServerStatus =>
  Object.prototype.hasOwnProperty.call(STATUS_LABELS, status);

/** Label + colour of an internal status; an unknown one renders as-is, in grey. */
export function verdaGpuStatusLabel(status: string): { label: string; color: VerdaLabelColor } {
  return isKnownStatus(status) ? STATUS_LABELS[status] : { label: status, color: 'grey' };
}

export function isVerdaGpuTerminal(status: string): boolean {
  return (VERDA_GPU_TERMINAL_STATUSES as string[]).includes(status);
}

/** CRITICAL red / WARNING orange / INFO blue; a customer notice (no severity) grey. */
export function severityColor(severity: string | null): VerdaLabelColor {
  switch (severity) {
    case 'CRITICAL':
      return 'red';
    case 'WARNING':
      return 'orange';
    case 'INFO':
      return 'blue';
    default:
      return 'grey';
  }
}

/** The worst severity among attention items, or `null` when there are none. */
export function worstSeverity(items: VerdaGpuAttentionItem[]): 'CRITICAL' | 'WARNING' | null {
  if (items.some((item) => item.severity === 'CRITICAL')) {
    return 'CRITICAL';
  }
  return items.length > 0 ? 'WARNING' : null;
}

/** `true` when a 6-dp margin string is ≤ 0 (the backend's `negative_margin` rule). Display only. */
export function isNonPositiveMargin(margin: string): boolean {
  const n = Number(margin);
  return margin.trim() !== '' && Number.isFinite(n) && n <= 0;
}

/**
 * The marker shown on a Servers row whose namespace differs from this installation's (the list's):
 * provider actions are refused for it. `null` when the row belongs here.
 */
export function namespaceMarker(rowNamespace: string, listNamespace: string | null): string | null {
  if (listNamespace === null) {
    return 'No installation tag here: no provider action possible';
  }
  return rowNamespace === listNamespace ? null : `Another installation (${rowNamespace})`;
}

// ---- Action enable rules ---------------------------------------------------------------------------

export interface ActionAvailability {
  enabled: boolean;
  /** Why the action is disabled (tooltip); `null` when enabled. */
  reason: string | null;
}

const ENABLED: ActionAvailability = { enabled: true, reason: null };
const disabled = (reason: string): ActionAvailability => ({ enabled: false, reason });

/** Mark not created is refused before this age (from `deploy_attempted_at ?? started_at`). */
export const MARK_NOT_CREATED_MIN_AGE_MS = 30 * 60 * 1000;

/** A claimed invoice becomes resolvable once its claim is this old (a younger create may be in flight). */
export const INVOICE_STALE_CLAIM_MS = 60 * 60 * 1000;

/**
 * Destroy: a real delete at Verda. `provisioning` / `running` / `unpaid`; `destroying` and `lost` only
 * with a recorded Verda instance id.
 */
export function destroyAvailability(server: VerdaGpuServer): ActionAvailability {
  switch (server.status) {
    case 'provisioning':
    case 'running':
    case 'unpaid':
      return ENABLED;
    case 'destroying':
    case 'lost':
      return server.verda.instance_id !== null
        ? ENABLED
        : disabled(`No Verda instance id is recorded for this ${server.status} server: nothing to delete at Verda`);
    case 'ordering':
    case 'deploy_unconfirmed':
      return disabled(
        'This start is still being reconciled (no Verda instance id yet): wait for the meter, or use Mark not created once you are sure nothing exists at Verda',
      );
    default:
      return disabled(`Nothing to delete at Verda for a ${server.status} server`);
  }
}

/**
 * Mark not created: an unconfirmed start (`ordering` / `deploy_unconfirmed`) with no Verda instance id,
 * at least 30 minutes after `deploy_attempted_at ?? started_at`.
 */
export function markNotCreatedAvailability(server: VerdaGpuServer, now: number): ActionAvailability {
  if (server.status !== 'ordering' && server.status !== 'deploy_unconfirmed') {
    return disabled('Only an unconfirmed start (ordering or deploy_unconfirmed) can be marked as not created');
  }
  if (server.verda.instance_id !== null) {
    return disabled('A Verda instance id is recorded for this start: it exists');
  }
  const since = Date.parse(server.deploy_attempted_at ?? server.started_at);
  if (Number.isNaN(since) || now - since < MARK_NOT_CREATED_MIN_AGE_MS) {
    return disabled('Too early: the meter is still reconciling this start');
  }
  return ENABLED;
}

/** Refund hour by hour: only once billing is over (`lost`, `failed`, `destroyed`). */
export function refundAvailability(server: VerdaGpuServer): ActionAvailability {
  return isVerdaGpuTerminal(server.status)
    ? ENABLED
    : disabled(
        'Only a lost, failed or destroyed server can be refunded hour by hour: for a gesture on a live server, adjust the wallet',
      );
}

/** Resolve: one-shot, on a `lost` / `failed` / `destroyed` server not resolved yet. */
export function resolveAvailability(server: VerdaGpuServer): ActionAvailability {
  if (!isVerdaGpuTerminal(server.status)) {
    return disabled(`Only a lost, failed or destroyed server can be resolved (this one is ${server.status})`);
  }
  return server.resolution === null ? ENABLED : disabled('This server is already resolved');
}

/** The staff-facing message for a destroy outcome; a `lost` row stays lost whatever happens. */
export function destroyOutcomeMessage(outcome: VerdaDestroyOutcome | string, wasLost: boolean): string {
  if (wasLost) {
    switch (outcome) {
      case 'deleted':
        return 'Nothing exists at the provider any more; the row stays lost';
      case 'accepted':
        return 'The provider accepted the deletion; the row stays lost';
      default:
        return 'Could not reach the provider — re-send Destroy';
    }
  }
  switch (outcome) {
    case 'deleted':
      return 'Deleted at the provider — confirmed gone';
    case 'accepted':
      return 'Deletion accepted — confirmed at the next meter run';
    default:
      return 'Could not reach the provider — the meter will retry';
  }
}

// ---- Refunds ---------------------------------------------------------------------------------------

/** The hour rows newest first (by `hour_start`, then id), whatever order they came in. */
export function sortUsageNewestFirst(rows: VerdaGpuServerUsageRow[]): VerdaGpuServerUsageRow[] {
  return [...rows].sort((a, b) => {
    const diff = Date.parse(b.hour_start) - Date.parse(a.hour_start);
    return Number.isNaN(diff) || diff === 0 ? b.id - a.id : diff;
  });
}

/**
 * What `{ all: true }` refunds: walking newest first, every not-yet-refunded hour until the first
 * INVOICED one (the backend stops there — an invoiced hour takes a credit note in Facturier).
 */
export function refundAllRange(rows: VerdaGpuServerUsageRow[]): VerdaGpuServerUsageRow[] {
  const sorted = sortUsageNewestFirst(rows);
  const firstInvoiced = sorted.findIndex((row) => row.invoice_id !== null);
  const beforeInvoiced = firstInvoiced === -1 ? sorted : sorted.slice(0, firstInvoiced);
  return beforeInvoiced.filter((row) => row.refunded_at === null);
}

/**
 * The starting points offered for "From this hour to the newest": only unrefunded rows NEWER than
 * the newest invoiced row (a range reaching an invoiced hour is refused outright). Newest first.
 */
export function refundStartCandidates(rows: VerdaGpuServerUsageRow[]): VerdaGpuServerUsageRow[] {
  return refundAllRange(rows);
}

/**
 * What `{ from_usage_id }` refunds: every unrefunded hour from that row to the newest. `null` when the
 * row is not an offered starting point.
 */
export function refundFromRange(rows: VerdaGpuServerUsageRow[], fromUsageId: number): VerdaGpuServerUsageRow[] | null {
  const candidates = refundStartCandidates(rows);
  const index = candidates.findIndex((row) => row.id === fromUsageId);
  return index === -1 ? null : candidates.slice(0, index + 1);
}

/** Count and exact Σ `debit_ttc` of the hours a refund would credit (`total` null if unknowable). */
export function refundPreview(range: VerdaGpuServerUsageRow[]): { count: number; total: string | null } {
  return { count: range.length, total: sumMoney(range.map((row) => row.debit_ttc)) };
}

// ---- Invoices --------------------------------------------------------------------------------------

/** claimed blue / issued orange / paid green / parked red / closed grey. */
export function invoiceStatusColor(status: string): VerdaLabelColor {
  switch (status) {
    case 'claimed':
      return 'blue';
    case 'issued':
      return 'orange';
    case 'paid':
      return 'green';
    case 'parked':
      return 'red';
    default:
      return 'grey';
  }
}

/**
 * Link and Release apply to an invoice with no Facturier id that is `parked`, or `claimed` for at
 * least 60 minutes (a younger claim's create may still be in flight).
 */
export function invoiceResolvability(invoice: VerdaGpuInvoice, now: number): ActionAvailability {
  if (invoice.facturier_invoice_id !== null) {
    return disabled('This invoice already has a Facturier invoice');
  }
  if (invoice.status === 'parked') {
    return ENABLED;
  }
  if (invoice.status === 'claimed') {
    const claimedAt = invoice.claimed_at === null ? NaN : Date.parse(invoice.claimed_at);
    return !Number.isNaN(claimedAt) && now - claimedAt >= INVOICE_STALE_CLAIM_MS
      ? ENABLED
      : disabled('The claim is less than 60 minutes old: its create may still be in flight');
  }
  return disabled(
    `Only a parked invoice, or a claim older than 60 minutes, can be linked or released (this one is ${invoice.status})`,
  );
}

/** Close: `parked` only (including a row that already has a Facturier id). */
export function invoiceCloseAvailability(invoice: VerdaGpuInvoice): ActionAvailability {
  return invoice.status === 'parked'
    ? ENABLED
    : disabled(`Only a parked invoice can be closed (this one is ${invoice.status})`);
}

// ---- Findings --------------------------------------------------------------------------------------

/**
 * Where a finding's subject links to, or `null` for plain text: a server → its detail; an invoice →
 * the Invoices tab filtered to it (never `staff_invoice_released`: that invoice was deleted); a
 * `staff_runway_*` project finding → the Balance tab. Instances (crc32) and clients are not linkable.
 */
export function findingLink(finding: VerdaFinding): string | null {
  switch (finding.subject_type) {
    case 'server':
      return finding.subject_id > 0 ? `/admin/verda/servers/${finding.subject_id}` : null;
    case 'invoice':
      return finding.stage === 'staff_invoice_released' || finding.subject_id <= 0
        ? null
        : `/admin/verda?tab=invoices&invoice=${finding.subject_id}`;
    case 'project':
      return finding.stage.startsWith('staff_runway_') ? '/admin/verda?tab=balance' : null;
    default:
      return null;
  }
}

// ---- Cost ledger -----------------------------------------------------------------------------------

const RUNWAY_FORMATTER = new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 2 });

/**
 * The runway cell: below 24 h a warning (starts are refused), below 6 h danger; `null` is "∞" when
 * nothing is billing (burn 0 with a known balance) and otherwise "Unknown" as a warning — never danger.
 */
export function runwayDisplay(entry: Pick<VerdaCostLedgerEntry, 'runway_hours' | 'burn_per_hour' | 'verda_balance'>): {
  text: string;
  severity: 'danger' | 'warning' | null;
} {
  if (entry.runway_hours === null) {
    const idle =
      entry.burn_per_hour !== null &&
      entry.burn_per_hour.trim() !== '' &&
      Number(entry.burn_per_hour) === 0 &&
      entry.verda_balance !== null;
    return idle ? { text: '∞', severity: null } : { text: 'Unknown', severity: 'warning' };
  }
  const hours = Number(entry.runway_hours);
  if (!Number.isFinite(hours)) {
    return { text: entry.runway_hours, severity: 'warning' };
  }
  const text = `${RUNWAY_FORMATTER.format(hours)} h`;
  if (hours < 6) {
    return { text, severity: 'danger' };
  }
  return { text, severity: hours < 24 ? 'warning' : null };
}

// ---- Client-side search ----------------------------------------------------------------------------

const includesQuery = (query: string, ...fields: (string | null | undefined)[]): boolean =>
  fields.some((field) => typeof field === 'string' && field.toLowerCase().includes(query));

/** Servers tab: hostname, client name, product label. `query` must be trimmed + lowercased. */
export function matchesServerSearch(server: VerdaGpuServer, query: string): boolean {
  return query === '' || includesQuery(query, server.hostname, server.client.name, server.product?.label);
}

/** Findings tab: stage, subject, message. */
export function matchesFindingSearch(finding: VerdaFinding, query: string): boolean {
  return query === '' || includesQuery(query, finding.stage, finding.subject, finding.message);
}

/** Invoices tab: search marker (`<hostname> <period>`), client name. */
export function matchesInvoiceSearch(invoice: VerdaGpuInvoice, query: string): boolean {
  return query === '' || includesQuery(query, invoice.search_marker, invoice.client.name);
}
